import { useEffect, useState } from 'react'
import {
  projectFileBytes,
  type Attachment as Att,
} from '../transport/api'
import { Markdown } from './Markdown'
import { formatBytes as human } from '../ui/bytes'
import { useBlobUrl } from '../ui/useBlobUrl'
import { withBlobUrl } from '../ui/blobUrl'

/**
 * A `.pfile` attachment card inside a project message. Class names are the original's so
 * the ported CSS applies unchanged.
 *
 * Files are end-to-end encrypted, so a RECIPIENT does not have the blob until it is
 * fetched and decrypted locally — `/blob` returns 409 `not_ready` before that. The card
 * therefore has real states: pending → downloading → ready (or failed / expired). Showing
 * a View button before the blob exists would just 409.
 *
 * `sync_state` is not carried in the card, so for incoming files the authoritative state
 * is reconciled once from `/project/file/status`.
 */

function kindOf(mime: string | undefined): 'image' | 'video' | 'audio' | 'doc' | 'other' {
  const m = (mime ?? '').split(';')[0] ?? ''
  if (m.startsWith('image/')) return 'image'
  if (m.startsWith('video/')) return 'video'
  if (m.startsWith('audio/')) return 'audio'
  if (m.startsWith('text/') || m === 'application/json' || m.endsWith('markdown')) return 'doc'
  return 'other'
}

const ICON: Record<string, string> = {
  image: '🖼',
  video: '🎬',
  audio: '🎵',
  doc: '📄',
  other: '📎',
}

export function AttachmentCard({
  att,
  projectId,
  onOpenDoc,
}: {
  att: Att
  projectId: string
  onOpenDoc: (a: { url: string; name: string; mime: string }) => void
}) {
  const kind = kindOf(att.mime)
  const isMedia = kind === 'image' || kind === 'video'
  /*
   * ALWAYS READY, and that is a property of the backend rather than an optimism.
   *
   * A managed S3 attachment exists only once its claiming message is written, so there
   * is nothing for the recipient to pull and no window in which the bytes are absent.
   * The edge lane was the one with real states — pending → downloading → ready / failed
   * / expired, a Download button, a progress bar, and a `projectFileStatus` reconcile to
   * find out which — because it encrypted per recipient and each one fetched their own
   * copy. All of it went with that lane on 2026-09-02, along with `att.sync_state`.
   *
   * If an attachment ever CAN be not-ready again, the states come back here, not as a
   * spinner somewhere else: this card is where a person looks to find out.
   */
  const ready = true
  const [err, setErr] = useState<string | null>(null)



  /*
   * Bytes over the relay, on demand — the blob URL pointed at the page origin,
   * which proxies nothing of the edge's, so View/Open/Download were dead ends
   * (owner: 把 project 檔案圖的 chunk lane 也做了). The thumbnail tile was never
   * affected: it rides base64 in the message card itself.
   */
  /* The inline audio player needs a LIVE url, not a click — fetched once when
     ready, revoked on unmount like every other object URL in this app. */
  const { url: audioUrl } = useBlobUrl(
    kind === 'audio' && ready ? () => projectFileBytes(projectId, att.file_id) : null,
    [kind, ready, projectId, att.file_id],
  )

  const withBytes = async (use: (objectUrl: string) => void) => {
    try {
      withBlobUrl(await projectFileBytes(projectId, att.file_id), use)
    } catch (e) {
      setErr(String(e))
    }
  }

  const thumbSrc = att.thumb
    ? att.thumb.startsWith('data:')
      ? att.thumb
      : `data:${att.mime ?? 'image/png'};base64,${att.thumb}`
    : null

  /*
   * THE FULL IMAGE INLINE, not just the base64 thumb — owner, 2026-08-14:
   * Project Chat 的預覽圖沒有生效. `att.thumb` rides in the card only for the
   * sender's own uploads; a RECEIVED image card carries no thumb, so it drew a
   * bare 🖼 icon and no preview at all. A ready image has bytes on the relay
   * (the same ones View fetches), so fetch them once and show the real picture —
   * a guaranteed preview whichever direction the file came from. Object-URL
   * lifecycle mirrors the audio player's: made on ready, revoked on unmount.
   */
  const { url: imgUrl } = useBlobUrl(
    kind === 'image' && ready ? () => projectFileBytes(projectId, att.file_id) : null,
    [kind, ready, projectId, att.file_id],
  )

  const previewSrc = imgUrl ?? thumbSrc

  /*
   * THE DOC INLINE, like the image above — owner via avg-main, 2026-08-19:
   * .md/text/json 附件直接顯示在訊息裡. A document you have to click through
   * to read is a document nobody reads in a conversation. Same byte lane and
   * object lifecycle as the image preview; markdown renders as markdown, the
   * rest as preformatted text, both capped by CSS with their own scroll.
   *
   * BOUNDED: past 256 KB the card stays a card — the attachments lane takes
   * .md up to 25 MB, and a novel inlined into the stream buries the room. The
   * View button (FileViewer) owns the big ones.
   */
  const INLINE_DOC_MAX = 262_144
  const isMd = (att.mime ?? '').endsWith('markdown') || (att.name ?? '').toLowerCase().endsWith('.md')
  const [docText, setDocText] = useState<string | null>(null)
  useEffect(() => {
    if (kind !== 'doc' || !ready) return
    if ((att.size ?? 0) > INLINE_DOC_MAX) return
    let alive = true
    projectFileBytes(projectId, att.file_id)
      .then((blob) => blob.text())
      .then((t) => {
        if (alive) setDocText(t)
      })
      .catch(() => {
        /* the card's View/Download remain; inline preview is a bonus, not a gate */
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, projectId, att.file_id])

  /*
   * Media opens in the app's cover-panel viewer, not a new tab — owner,
   * 2026-08-14: 點擊檔案 / image 直接用與 agent terminal 一樣的方式蓋板顯示,
   * 而非開一個新的分頁. `onOpenDoc` routes any kind to the FileViewer; only ready
   * media has bytes to hand it, so the thumbnail is a live button just then.
   */
  /*
   * AN IMAGE HANDS OVER THE URL IT IS ALREADY SHOWING, not a fresh one.
   *
   * `withBytes` mints through withBlobUrl, which revokes on a 30-SECOND timer — long
   * enough for a click-through, far too short for the thing the owner asked for on
   * 2026-09-15: open the picture, look at it, then press Download. The viewer's
   * Download is an `<a href={src} download>`, so past the grace it silently produces
   * nothing, and until now that did not bite because the card kept a Download of its
   * own that re-fetched. Removing that button makes the overlay the only road, so the
   * road has to stay open.
   *
   * `imgUrl` is the object URL this card is ALREADY displaying (useBlobUrl above): it
   * lives as long as the card is mounted and is revoked in its teardown, so nothing new
   * owns anything. It also saves fetching the same bytes twice. The fallback covers the
   * moment before it has loaded, where the tile is still showing the base64 thumb.
   *
   * Honest limit: if the message unmounts while the overlay is open, that URL goes with
   * it. Strictly better than 30 seconds, and the same lifetime the picture behind the
   * overlay already has.
   */
  const openMedia = () => {
    if (kind === 'image' && imgUrl) {
      onOpenDoc({ url: imgUrl, name: att.name ?? 'file', mime: att.mime ?? '' })
      return
    }
    void withBytes((u) => onOpenDoc({ url: u, name: att.name ?? 'file', mime: att.mime ?? '' }))
  }
  const thumbClickable = ready && (isMedia || kind === 'audio')

  return (
    <div className={`pfile pfile-${kind}${isMedia ? ' pfile-media-card' : ''}`} data-file-id={att.file_id}>
      <div
        className={`pfile-thumb${previewSrc ? '' : ' noimg'}${thumbClickable ? ' tap' : ''}`}
        {...(thumbClickable
          ? { role: 'button', tabIndex: 0, onClick: openMedia, title: `Open ${att.name ?? 'file'}` }
          : {})}
      >
        {previewSrc ? <img src={previewSrc} alt={att.name ?? ''} /> : ICON[kind]}
        {kind === 'video' && <span className="pfile-playbadge">▶</span>}
      </div>

      <div className="pfile-meta">
        {/* Image and video are clean tiles in the original: no filename, no type·size. */}
        {!isMedia && (
          <>
            <div className="pfile-name" title={att.name ?? ''}>
              {att.name ?? 'file'}
            </div>
            <div className="pfile-sub">
              {(att.mime ? att.mime.split(';')[0] : kind)} · {human(att.size)}
            </div>
          </>
        )}

        {/*
          * AN IMAGE HAS NO BUTTONS — owner, 2026-09-15: 顯示這個圖片的 Open 跟 Download
          * 按鈕是非常沒有必要的, 使用者只需要點擊圖片, 就出現蓋版的顯示畫面 … 基本上就是跟
          * File Browser 裡面瀏覽圖片的行為一模一樣.
          *
          * Both were already duplicates of things the picture itself does. "Open" ran the
          * same `openMedia` the tile runs when tapped, and Download repeated a control the
          * overlay has carried since the owner asked for it on 2026-09-13 ("在 X 按鈕的
          * 左邊放置一個 Download 按鈕" — FileViewer's DownloadControl, immediately left of
          * Close). So the picture is the button, and the overlay owns what you do next.
          *
          * IMAGE ONLY, deliberately. Video and audio keep theirs: video hands the viewer a
          * 30-second handover URL rather than one it is already displaying, and audio's
          * card is where its Download lives at all. An `other` file has no clickable tile,
          * so removing its buttons would leave no way to reach the bytes.
          */}
        <div className="pfile-act">
          {ready && kind !== 'image' ? (
            <>
              {kind === 'doc' ? (
                <button
                  type="button"
                  className="pfile-btn"
                  onClick={() =>
                    void withBytes((u) =>
                      onOpenDoc({ url: u, name: att.name ?? 'file', mime: att.mime ?? 'text/plain' }),
                    )
                  }
                >
                  View
                </button>
              ) : isMedia || kind === 'audio' ? (
                <button type="button" className="pfile-btn" onClick={openMedia}>
                  Open
                </button>
              ) : null}
              <button
                type="button"
                className="pfile-btn ghost"
                onClick={() =>
                  void withBytes((u) => {
                    const dl = document.createElement('a')
                    dl.href = u
                    dl.download = att.name ?? 'file'
                    dl.click()
                  })
                }
              >
                ⬇ Download
              </button>
            </>
          ) : null}
          {err && <div className="pfile-warn">{err}</div>}
        </div>

        {kind === 'audio' && ready && audioUrl && (
          // no captions track exists for user-shared audio (fossil eslint-disable removed 2026-09-02)
          <audio className="pfile-media" src={audioUrl} controls preload="metadata" />
        )}
      </div>

      {/* The doc, readable IN the message. Markdown as markdown; everything else
          as written. The block scrolls within its own cap (CSS), so a long doc
          never buries the conversation it was shared into. */}
      {docText !== null &&
        (isMd ? (
          <div className="pfile-read pmsg-body md">
            <Markdown body={docText} />
          </div>
        ) : (
          <pre className="pfile-read pfile-read-pre">{docText}</pre>
        ))}
    </div>
  )
}
